import { useCallback, useContext } from 'react';
import WSS from '../../../../shared/lib/wssAPI';
import {
  CHECKIDENTIFIER,
  CONNECTSTATUS,
  CLIENTREQUEST,
  SCANSTATUS,
  magtekResponseCode,
  magtekErrorMessage,
} from '../config';

import { CheckScannerContext } from '../context/CheckScannerContext';

let _requestKey = 1;

export const useCheckScanner = () => {
  const {
    status,
    setStatus,
    showModal,
    setShowModal,
    scanInfo,
    setScanInfo,
    showImageModal,
    setShowImageModal,
    errorInfo,
    setErrorInfo,
    saveCallback,
  } = useContext(CheckScannerContext);

  const init = useCallback(async () => {
    const initRequest = WSS.getWSRequest(
      WSS.RequestMethod.INIT,
      WSS.FunctionName.INIT,
      'JNIWriteToLog=true',
      WSS.Processor.MAGTEKEXCELLA,
      CHECKIDENTIFIER,
    );
    await WSS.sendWSPromise(initRequest);
  }, []);

  const getDeviceList = useCallback(async () => {
    try {
      await init();
      const RequestKey = String(_requestKey++);
      const getDeviceNamesRequest = WSS.getWSRequestForJSON(
        WSS.RequestMethod.EXEC,
        WSS.FunctionName.MAGTEK_EXCELLA_GET_DEVICE_NAMES,
        '',
        {
          RequestKey,
        },
        WSS.Processor.MAGTEKEXCELLA,
        CHECKIDENTIFIER,
      );
      const {
        value:
     { MagtekResponse = {} } = {},
      } = await WSS.sendWSPromise(getDeviceNamesRequest);
      const { deviceNames, requestKey, callStatus } = MagtekResponse;
      if (requestKey === RequestKey && callStatus === magtekResponseCode.success && deviceNames) {
        setStatus(CONNECTSTATUS.connect);
        return deviceNames;
      }
      setStatus(CONNECTSTATUS.disconnect);
    } catch (error) {
      setStatus(CONNECTSTATUS.disconnect);
    }
    return '';
  }, [ init, setStatus ]);

  const handleError = useCallback((text) => {
    setShowModal(false);
    setErrorInfo({
      show: true,
      text,
    });
  }, [ setShowModal, setErrorInfo ]);

  const handleSuccess = useCallback(({
    micrDecode, micrCheckNumber, ...rest
  }) => {
    if (micrDecode !== magtekResponseCode.micrDecode) {
      handleError(magtekErrorMessage.success);
      return;
    }
    setScanInfo({
      _checkNumber: micrCheckNumber,
      scanStatus: SCANSTATUS.scanned,
      micrCheckNumber,
      ...rest,
    });
    setErrorInfo({
      show: false,
      text: '',
    });
  }, [ handleError, setScanInfo, setErrorInfo ]);

  const processDocument = useCallback(async ({ endorsementContent }) => {
    try {
      const DeviceName = await getDeviceList();
      if (!DeviceName) return;
      setShowModal(true);
      setScanInfo({
        ...scanInfo,
        scanStatus: SCANSTATUS.init,
      });
      const scanCheckRequest = WSS.getWSRequestForJSON(
        WSS.RequestMethod.EXEC,
        WSS.FunctionName.MAGTEK_EXCELLA_SCAN_CHECK,
        '',
        {
          RequestKey: String(_requestKey++),
          DeviceName,
          PrintMessageBack: endorsementContent || '',
          ...CLIENTREQUEST,
        },
        WSS.Processor.MAGTEKEXCELLA,
        CHECKIDENTIFIER);

      const {
        value:
         { MagtekResponse } = { MagtekResponse: {} },
      } = await WSS.sendWSPromise(scanCheckRequest);
      const {
        errorMessage,
        resultCode,
        deviceReturnCode,
      } = MagtekResponse;
      if (!errorMessage && resultCode === magtekResponseCode.success) {
        const {
          micrCheckNumber,
          micrCheckCountry: checkCountry,
          micrBankNumber: bankNumber,
          micrAccountNumber: accountNumber,
          micrTransitNumber: routingNumber,
          micrCheckType: checkType,
          micrRaw,
          frontImage,
          backImage,
          micrDecode,
        } = MagtekResponse;

        handleSuccess({
          micrDecode,
          micrRaw,
          frontImage,
          backImage,
          micrCheckNumber,
          checkCountry,
          bankNumber,
          accountNumber,
          routingNumber,
          checkType,
        });
        return;
      }
      if (resultCode === magtekResponseCode.usbGetDataFailed) {
        handleError(magtekErrorMessage[resultCode]);
        return;
      }
      if (deviceReturnCode === magtekResponseCode.timeout) {
        handleError(magtekErrorMessage[deviceReturnCode]);
        return;
      }
      handleError(magtekErrorMessage.default);
    } catch (error) {
      setShowModal(false);
    }
  }, [
    getDeviceList,
    scanInfo,
    setScanInfo,
    setShowModal,
    handleError,
    handleSuccess,
  ]);

  return {
    status,
    processDocument,
    getDeviceList,
    showModal,
    setShowModal,
    scanInfo,
    setScanInfo,
    saveCallback,
    showImageModal,
    setShowImageModal,
    errorInfo,
    setErrorInfo,
  };
};
